import type { ReactNode } from "react";

import { cn } from "../../lib/utils";

export type PlusBadgeVariant = "locked" | "active";
export type PlusBadgeSize = "sm" | "md";

export interface PlusBadgeProps {
  className?: string;
  size?: PlusBadgeSize;
  variant?: PlusBadgeVariant;
}

// PlusBadge is the canonical inline visual for the Sill Plus tier. It renders
// the literal word "Plus" with two chrome styles:
//   - "locked"  → dashed primary outline, transparent fill — used when a
//                 free-tier user sees a gated affordance and we want to hint
//                 the upgrade.
//   - "active"  → solid primary fill — used when the current profile already
//                 holds the Plus entitlement (self or household).
// No emoji, no lock glyph: the text alone carries the meaning and matches the
// hard-coded brand voice in the design spec (docs/superpowers/specs/
// 2026-05-12-sill-plus-design.md §4.5).
export function PlusBadge({
  className,
  size = "md",
  variant = "locked",
}: PlusBadgeProps): ReactNode {
  return (
    <span
      aria-label="Plus"
      className={cn(
        "inline-flex items-center justify-center rounded-full font-semibold uppercase tracking-[0.14em]",
        size === "sm" ? "min-h-6 px-2 py-0.5 text-[0.625rem]" : "min-h-7 px-2.5 py-0.5 text-xs",
        variant === "locked" &&
          "border border-dashed border-primary/60 bg-transparent text-primary",
        variant === "active" && "border border-transparent bg-primary text-primary-foreground",
        className,
      )}
      data-variant={variant}
    >
      Plus
    </span>
  );
}
